import React, {useState} from 'react';
import {motion, AnimatePresence} from 'framer-motion';
import {FaLock, FaEye, FaEyeSlash, FaCheckCircle, FaExclamationTriangle} from 'react-icons/fa';
import LuxuryButton from '../../components/ui/LuxuryButton';
import LuxuryInput from '../../components/ui/LuxuryInput';
import {designTokens} from "../../theme/designTokens";

const ChangePasswordModal = ({isOpen, onClose, onSubmit}) => {
    const [formData, setFormData] = useState({
        currentPassword: '', newPassword: '', confirmPassword: ''
    });
    const [showPassword, setShowPassword] = useState({
        current: false, new: false, confirm: false
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);

        // Validation
        if (!formData.currentPassword || !formData.newPassword || !formData.confirmPassword) {
            setError('Please fill in all fields');
            return;
        }

        if (formData.newPassword.length < 8) {
            setError('New password must be at least 8 characters long');
            return;
        }

        if (formData.newPassword !== formData.confirmPassword) {
            setError('New passwords do not match');
            return;
        }

        if (formData.currentPassword === formData.newPassword) {
            setError('New password must be different from current password');
            return;
        }

        setLoading(true);
        try {
            // Call the onSubmit prop which should handle the API call
            await onSubmit(formData);
            setSuccess('Password changed successfully!');

            // Clear form and close modal after success
            setTimeout(() => {
                setFormData({
                    currentPassword: '', newPassword: '', confirmPassword: ''
                });
                onClose();
            }, 2000);
        } catch (err) {
            setError(err.message || 'Failed to change password');
        } finally {
            setLoading(false);
        }
    };

    const togglePasswordVisibility = (field) => {
        setShowPassword(prev => ({
            ...prev, [field]: !prev[field]
        }));
    };

    const getPasswordStrength = (password) => {
        if (!password) return 0;

        let strength = 0;
        if (password.length >= 8) strength++;
        if (/[A-Z]/.test(password)) strength++;
        if (/[a-z]/.test(password)) strength++;
        if (/[0-9]/.test(password)) strength++;
        if (/[^A-Za-z0-9]/.test(password)) strength++;

        return strength;
    };

    const passwordStrength = getPasswordStrength(formData.newPassword);

    const strengthLabels = ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong'];
    const strengthColors = ['text-red-500', 'text-orange-500', 'text-yellow-500', 'text-lime-500', 'text-green-500'];
    const barColors = ['bg-red-500', 'bg-orange-500', 'bg-yellow-500', 'bg-lime-500', 'bg-green-500'];

    if (!isOpen) return null;

    return (<AnimatePresence>
            <motion.div
                initial={{opacity: 0}}
                animate={{opacity: 1}}
                exit={{opacity: 0}}
                className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                onClick={onClose}
            >
                <motion.div
                    initial={{opacity: 0, scale: 0.95}}
                    animate={{opacity: 1, scale: 1}}
                    exit={{opacity: 0, scale: 0.95}}
                    transition={{type: "spring", damping: 25, stiffness: 300}}
                    className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="p-6 border-b border-nude/30">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-sage/10 flex items-center justify-center">
                                <FaLock className="w-5 h-5 text-sage"/>
                            </div>
                            <div>
                                <h2 className="text-xl font-medium text-charcoal">Change Password</h2>
                                <p className="text-sm text-charcoal/60">Update your account password</p>
                            </div>
                        </div>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="p-6 space-y-6">
                        {/* Notifications */}
                        <AnimatePresence>
                            {error && (<motion.div
                                    initial={{opacity: 0, y: -10}}
                                    animate={{opacity: 1, y: 0}}
                                    exit={{opacity: 0, y: -10}}
                                    className="p-4 bg-error/10 border border-error/20 rounded-xl flex items-start gap-3"
                                >
                                    <FaExclamationTriangle className="w-5 h-5 text-error flex-shrink-0 mt-0.5"/>
                                    <p className="text-sm text-error flex-1">{error}</p>
                                </motion.div>)}

                            {success && (<motion.div
                                    initial={{opacity: 0, y: -10}}
                                    animate={{opacity: 1, y: 0}}
                                    exit={{opacity: 0, y: -10}}
                                    className="p-4 bg-sage/10 border border-sage/20 rounded-xl flex items-start gap-3"
                                >
                                    <FaCheckCircle className="w-5 h-5 text-sage flex-shrink-0 mt-0.5"/>
                                    <p className="text-sm text-sage flex-1">{success}</p>
                                </motion.div>)}
                        </AnimatePresence>

                        {/* Current Password */}
                        <div>
                            <label className="block text-sm font-medium text-charcoal/80 mb-2">
                                Current Password
                            </label>
                            <div className="relative">
                                <LuxuryInput
                                    type={showPassword.current ? "text" : "password"}
                                    value={formData.currentPassword}
                                    onChange={(e) => setFormData(prev => ({...prev, currentPassword: e.target.value}))}
                                    placeholder="Enter current password"
                                    icon={FaLock}
                                    className="pr-10"
                                />
                            </div>
                        </div>

                        {/* New Password */}
                        <div>
                            <label className="block text-sm font-medium text-charcoal/80 mb-2">
                                New Password
                            </label>
                            <div className="relative">
                                <LuxuryInput
                                    type={showPassword.new ? "text" : "password"}
                                    value={formData.newPassword}
                                    onChange={(e) => setFormData(prev => ({...prev, newPassword: e.target.value}))}
                                    placeholder="Enter new password"
                                    icon={FaLock}
                                    className="pr-10"
                                />
                            </div>

                            {/* Password Strength Meter */}
                            {formData.newPassword && (<motion.div
                                    initial={{opacity: 0, height: 0}}
                                    animate={{opacity: 1, height: 'auto'}}
                                    className="mt-3 space-y-2"
                                >
                                    <div className="h-2 bg-nude/20 rounded-full overflow-hidden">
                                        <motion.div
                                            initial={{width: 0}}
                                            animate={{width: `${passwordStrength * 20}%`}}
                                            transition={{duration: 0.5}}
                                            className={`h-full ${barColors[passwordStrength - 1] || 'bg-red-500'}`}
                                        />
                                    </div>
                                    <p className={`text-xs ${strengthColors[passwordStrength - 1] || 'text-charcoal/50'}`}>
                                        Password strength: {strengthLabels[passwordStrength - 1] || 'Very Weak'}
                                    </p>
                                </motion.div>)}
                        </div>

                        {/* Confirm Password */}
                        <div>
                            <label className="block text-sm font-medium text-charcoal/80 mb-2">
                                Confirm New Password
                            </label>
                            <div className="relative">
                                <LuxuryInput
                                    type={showPassword.confirm ? "text" : "password"}
                                    value={formData.confirmPassword}
                                    onChange={(e) => setFormData(prev => ({...prev, confirmPassword: e.target.value}))}
                                    placeholder="Confirm new password"
                                    icon={FaLock}
                                    className="pr-10"
                                />

                            </div>
                            {formData.newPassword && formData.confirmPassword && (
                                <p className={`text-xs mt-2 ${formData.newPassword === formData.confirmPassword ? 'text-sage' : 'text-error'}`}>
                                    {formData.newPassword === formData.confirmPassword ? '✓ Passwords match' : '✗ Passwords do not match'}
                                </p>)}
                        </div>

                        {/* Password Requirements */}
                        <div className="bg-nude/5 rounded-xl p-4">
                            <h4 className="text-sm font-medium text-charcoal/80 mb-2">Password Requirements:</h4>
                            <ul className="text-xs text-charcoal/60 space-y-1">
                                <li className={`flex items-center gap-2 ${formData.newPassword.length >= 8 ? 'text-sage' : ''}`}>
                                    {formData.newPassword.length >= 8 ? '✓' : '○'} At least 8 characters
                                </li>
                                <li className={`flex items-center gap-2 ${/[A-Z]/.test(formData.newPassword) ? 'text-sage' : ''}`}>
                                    {/[A-Z]/.test(formData.newPassword) ? '✓' : '○'} One uppercase letter
                                </li>
                                <li className={`flex items-center gap-2 ${/[a-z]/.test(formData.newPassword) ? 'text-sage' : ''}`}>
                                    {/[a-z]/.test(formData.newPassword) ? '✓' : '○'} One lowercase letter
                                </li>
                                <li className={`flex items-center gap-2 ${/[0-9]/.test(formData.newPassword) ? 'text-sage' : ''}`}>
                                    {/[0-9]/.test(formData.newPassword) ? '✓' : '○'} One number
                                </li>
                            </ul>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-3 pt-4">
                            <LuxuryButton
                                type="button"
                                variant="outline"
                                size="medium"
                                onClick={onClose}
                                disabled={loading}
                                className="flex-1"
                            >
                                Cancel
                            </LuxuryButton>
                            <LuxuryButton
                                type="submit"
                                variant="primary"
                                size="medium"
                                loading={loading}
                                disabled={loading}
                                style={{
                                    background: designTokens.colors.gradients.champagne,
                                }}
                            >
                                Update Password
                            </LuxuryButton>
                        </div>
                    </form>

                    {/* Footer */}
                    <div className="p-4 border-t border-nude/30 bg-nude/5 text-center">
                        <p className="text-xs text-charcoal/50">
                            For security, we'll log you out of all other devices after changing your password.
                        </p>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>);
};

export default ChangePasswordModal;